import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Shield, Mail, Lock, User, AlertCircle } from 'lucide-react';

export default function Register() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    const result = await register(email, password, name);
    
    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex login-bg">
      {/* Left side - Background with overlay */}
      <div className="hidden lg:flex lg:w-1/2 relative">
        <div className="absolute inset-0 bg-gradient-to-br from-[#00CED1]/60 to-[#0a0d14]/95" />
        <div className="relative z-10 flex flex-col justify-center px-12 text-white">
          <div className="flex items-center gap-3 mb-8">
            <Shield className="w-12 h-12 text-[#E8520F]" />
            <span className="text-3xl font-bold font-['Outfit']">GauriLabs Commander</span>
          </div>
          <h1 className="text-4xl font-bold mb-4 font-['Outfit']">
            Join the Future of DeFi
          </h1>
          <p className="text-lg text-white/80 max-w-md">
            Create your account to access advanced market automation, ZK-privacy features, and multi-chain wallet management.
          </p>
        </div>
      </div>

      {/* Right side - Register form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-[hsl(220,20%,8%)]">
        <Card className="w-full max-w-md border border-[hsl(220,16%,18%)] bg-[hsl(220,18%,11%)] shadow-none">
          <CardHeader className="text-center pb-2">
            <div className="flex items-center justify-center gap-2 mb-4 lg:hidden">
              <Shield className="w-8 h-8 text-[#E8520F]" />
              <span className="text-xl font-bold font-['Outfit'] text-white">GauriLabs</span>
            </div>
            <CardTitle className="text-2xl font-['Outfit'] text-white">Create Account</CardTitle>
            <CardDescription className="text-[hsl(215,16%,56%)]">
              Start your journey with GauriLabs Commander
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm" data-testid="register-error">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="name" className="text-[hsl(210,20%,85%)]">Full Name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(215,16%,45%)]" />
                  <Input
                    id="name"
                    type="text"
                    placeholder="John Doe"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="pl-10 border-[hsl(220,16%,22%)] bg-[hsl(220,16%,14%)] text-white placeholder:text-[hsl(215,16%,40%)] focus:ring-2 focus:ring-[#E8520F] focus:border-transparent"
                    required
                    data-testid="register-name-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-[hsl(210,20%,85%)]">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(215,16%,45%)]" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 border-[hsl(220,16%,22%)] bg-[hsl(220,16%,14%)] text-white placeholder:text-[hsl(215,16%,40%)] focus:ring-2 focus:ring-[#E8520F] focus:border-transparent"
                    required
                    data-testid="register-email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-[hsl(210,20%,85%)]">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(215,16%,45%)]" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="--------"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 border-[hsl(220,16%,22%)] bg-[hsl(220,16%,14%)] text-white placeholder:text-[hsl(215,16%,40%)] focus:ring-2 focus:ring-[#E8520F] focus:border-transparent"
                    required
                    data-testid="register-password-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-[hsl(210,20%,85%)]">Confirm Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(215,16%,45%)]" />
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="--------"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pl-10 border-[hsl(220,16%,22%)] bg-[hsl(220,16%,14%)] text-white placeholder:text-[hsl(215,16%,40%)] focus:ring-2 focus:ring-[#E8520F] focus:border-transparent"
                    required
                    data-testid="register-confirm-password-input"
                  />
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full bg-[#E8520F] hover:bg-[#C9440C] text-white"
                disabled={loading}
                data-testid="register-submit-btn"
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <div className="spinner w-4 h-4" />
                    <span>Creating account...</span>
                  </div>
                ) : (
                  'Create Account'
                )}
              </Button>

              <p className="text-center text-sm text-[hsl(215,16%,56%)]">
                Already have an account?{' '}
                <Link to="/login" className="text-[#E8520F] hover:underline font-medium" data-testid="login-link">
                  Sign in
                </Link>
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
